import { DynamicConfig } from '@domain/configuration/types';
import { useMemo } from 'react';
import { useSalesPortalDynamicConfigLoader } from '@/experiences/sales-portal/blocs/loaders/use-sales-portal-dynamic-config-loader';

export type { DynamicConfig, Experiment } from '@domain/configuration/types';

/**
 * Parameters for fetching Sales Portal dynamic config.
 */
interface SalesPortalDynamicConfigParams {
  businessOrganizationUUID: string;
  chatUUID?: string | null;
  supplierUUID: string;
}

/**
 * Result object returned by useSalesPortalDynamicConfig.
 */
interface SalesPortalDynamicConfigResult {
  chatDynamicConfig: DynamicConfig | null;
  loading: boolean;
  supplierDynamicConfig: DynamicConfig | null;
}

/**
 * Hook to access dynamic config for Sales Portal.
 * Uses the dynamic config loader to fetch data and provides access to
 * supplier config and chat-specific config.
 *
 * @deprecated Backed by the org-wide loader that over-fetches every chat's
 * config for the business org (~3MB per call for large distributors), which
 * adds network load and blocks the main thread while Apollo normalizes it into
 * the cache. Use `useChatSalesPortalDynamicConfig` instead.
 *
 * @param params - The configuration parameters
 * @param params.businessOrganizationUUID - The business organization identifier
 * @param params.chatUUID - Optional chat identifier for chat-specific config
 * @param params.supplierUUID - The supplier identifier
 * @returns An object containing chat config, supplier config, and loading state
 *
 * @example
 * ```typescript
 * const { chatDynamicConfig, loading, supplierDynamicConfig } =
 *   useSalesPortalDynamicConfig({
 *     businessOrganizationUUID: 'org-123',
 *     chatUUID: 'chat-456',
 *     supplierUUID: 'supplier-789',
 *   });
 *
 * if (loading) {
 *   return <Loading />;
 * }
 *
 * // Use supplier config as fallback when chat config is not available
 * const config = chatDynamicConfig ?? supplierDynamicConfig;
 * ```
 */
export const useSalesPortalDynamicConfig = ({
  businessOrganizationUUID,
  chatUUID,
  supplierUUID,
}: SalesPortalDynamicConfigParams): SalesPortalDynamicConfigResult => {
  const { chatDynamicConfigMap, loading, supplierDynamicConfig } =
    useSalesPortalDynamicConfigLoader({
      businessOrganizationUUID,
      supplierUUID,
    });

  const chatDynamicConfig = useMemo(
    () => (chatUUID ? (chatDynamicConfigMap[chatUUID] ?? null) : null),
    [chatDynamicConfigMap, chatUUID],
  );

  return {
    chatDynamicConfig,
    loading,
    supplierDynamicConfig,
  };
};

/**
 * Hook to get a specific dynamic config value for Sales Portal.
 * Falls back through: chat config -> supplier config -> null.
 *
 * @deprecated Backed by the org-wide loader that over-fetches every chat's
 * config for the business org (~3MB per call for large distributors), which
 * adds network load and blocks the main thread while Apollo normalizes it into
 * the cache. Use `useChatSalesPortalDynamicConfigValue` instead.
 *
 * @param businessOrganizationUUID - The business organization identifier
 * @param supplierUUID - The supplier identifier
 * @param key - The dynamic config key to retrieve
 * @param chatUUID - Optional chat identifier for chat-specific config
 * @returns An object containing the config value and loading state
 *
 * @example
 * ```typescript
 * const { loading, value } = useSalesPortalDynamicConfigValue(
 *   'org-123',
 *   'supplier-789',
 *   'experiments',
 *   'chat-456',
 * );
 *
 * if (loading) {
 *   return <Loading />;
 * }
 *
 * // value is typed based on the key provided
 * console.log(value); // Experiment[] | null
 * ```
 */
export const useSalesPortalDynamicConfigValue = <K extends keyof DynamicConfig>(
  businessOrganizationUUID: string,
  supplierUUID: string,
  key: K,
  chatUUID: string | null | undefined,
): { loading: boolean; value: DynamicConfig[K] | null } => {
  const { chatDynamicConfig, loading, supplierDynamicConfig } =
    useSalesPortalDynamicConfig({
      businessOrganizationUUID,
      chatUUID,
      supplierUUID,
    });

  const value = useMemo(() => {
    if (loading) {
      return null;
    }
    return chatDynamicConfig?.[key] ?? supplierDynamicConfig?.[key] ?? null;
  }, [chatDynamicConfig, key, loading, supplierDynamicConfig]);

  return {
    loading,
    value,
  };
};
